import { useState, type ReactElement } from 'react';
import {
  FeatureTestRunsNestedTable,
  type FeatureTestRunActions,
} from '@/features/features/components/FeatureTestRunsNestedTable';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import type { TableV2Counts } from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { TagColor } from '@/shared/data/tags';
import type { FeatureDto, ReleaseListItemDto } from '@flowqa/shared';

type ReleaseFeatureItem = NonNullable<NonNullable<ReleaseListItemDto['features']>['items']>[number];

type ReleaseFeaturesNestedTableProps = {
  deleteRowPendingId: string | null;
  expanded: boolean;
  features: ReleaseFeatureItem[];
  projectId: string;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: FeatureDto) => void;
  onEditRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
};

const releaseFeaturesColgroup = (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="22%" />
    <col width="12%" />
    <col width="15%" />
    <col width="21%" />
    <col width="15%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const toNestedFeatureActionRow = (
  feature: ReleaseFeatureItem,
  fallbackProjectId: string,
): FeatureDto => ({
  id: feature.id,
  slug: feature.slug,
  name: feature.name,
  status: feature.status,
  plannedFor: feature.plannedFor ?? null,
  releasedAt: feature.releasedAt ?? null,
  projectId: feature.projectId ?? fallbackProjectId,
  testLatestRunPassed: feature.testLatestRunPassed ?? 0,
  testLatestRunFailed: feature.testLatestRunFailed ?? 0,
  testLatestRunErrored: feature.testLatestRunErrored ?? 0,
  testLatestRunSkipped: feature.testLatestRunSkipped ?? 0,
  testLatestRunUntested: feature.testLatestRunUntested ?? 0,
  tags: feature.tags ?? [],
  deletedAt: feature.deletedAt ?? null,
});

const buildFeatureTestCounts = (feature: ReleaseFeatureItem): TableV2Counts => ({
  total:
    (feature.testLatestRunPassed ?? 0) +
    (feature.testLatestRunFailed ?? 0) +
    (feature.testLatestRunErrored ?? 0) +
    (feature.testLatestRunSkipped ?? 0) +
    (feature.testLatestRunUntested ?? 0),
  passed: feature.testLatestRunPassed ?? 0,
  failed: feature.testLatestRunFailed ?? 0,
  errored: feature.testLatestRunErrored ?? 0,
  skipped: feature.testLatestRunSkipped ?? 0,
  untested: feature.testLatestRunUntested ?? 0,
});

const ReleaseFeatureNestedRow = ({
  deleteRowPendingId,
  feature,
  isLastVisibleRow,
  projectId,
  tagColorByName,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  feature: ReleaseFeatureItem;
  isLastVisibleRow: boolean;
  projectId: string;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: FeatureDto) => void;
  onEditRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const actionRow = toNestedFeatureActionRow(feature, projectId);
  const testCounts = buildFeatureTestCounts(feature);

  return (
    <>
      <TableRow
        sx={[
          tableV2NestedDepthSx[1].bodyRow,
          {
            '& > td': {
              borderBottom:
                expanded || isLastVisibleRow
                  ? 0
                  : (theme) => `1px solid ${theme.app.colors.border}`,
              height: `${String(tableV2NestedBodyRowHeightPx)}px`,
              py: s('sm'),
              verticalAlign: 'middle',
            },
          },
        ]}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse feature test runs' : 'Expand feature test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell href={`/features/${feature.slug}`} primary={feature.name} />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={feature.status} />
        </TableCell>
        <TableCell>
          <TableV2DateCell value={feature.plannedFor ?? null} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={testCounts} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={feature.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={feature.name}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Feature')}
            editTagsEntityNoun="feature"
            rowTags={feature.tags ?? []}
            onEdit={() => {
              onEditRow(actionRow);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row: actionRow, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === feature.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Feature')}
            onDelete={() => {
              onDeleteRow(actionRow);
            }}
            disableDelete={deleteRowPendingId === feature.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={7}>
          <FeatureTestRunsNestedTable
            childCount={testCounts.total}
            depth={2}
            expanded={expanded}
            featureId={feature.id}
            projectId={projectId}
            testRunActions={testRunActions}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const ReleaseFeaturesNestedTable = ({
  deleteRowPendingId,
  expanded,
  features,
  projectId,
  tagColorByName,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: ReleaseFeaturesNestedTableProps): ReactElement => (
  <TableV2NestedCollapse expanded={expanded}>
    <Box sx={tableV2NestedContentSx}>
      {features.length > 0 ? (
        <Box sx={tableV2NestedDepthSx[1].frame}>
          <Table aria-label="Release features" sx={tableV2NestedDepthSx[1].table}>
            {releaseFeaturesColgroup}
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableV2HeaderLabel label="" />
                </TableCell>
                <TableCell>
                  <TableV2HeaderLabel label="Feature" />
                </TableCell>
                <TableCell>
                  <TableV2HeaderLabel label="Status" />
                </TableCell>
                <TableCell>
                  <TableV2HeaderLabel label="Planned For" />
                </TableCell>
                <TableCell>
                  <TableV2HeaderLabel label="Testing Status" />
                </TableCell>
                <TableCell>
                  <TableV2HeaderLabel label="Tags" />
                </TableCell>
                <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
                  <TableV2ActionsHeaderIcon />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature, index) => (
                <ReleaseFeatureNestedRow
                  key={feature.id}
                  deleteRowPendingId={deleteRowPendingId}
                  feature={feature}
                  isLastVisibleRow={index === features.length - 1}
                  projectId={projectId}
                  tagColorByName={tagColorByName}
                  testRunActions={testRunActions}
                  updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                  onDeleteRow={onDeleteRow}
                  onEditRow={onEditRow}
                  onUpdateRowTagsColor={onUpdateRowTagsColor}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Text role="body" color="textSecondary">
          No associated features
        </Text>
      )}
    </Box>
  </TableV2NestedCollapse>
);
